import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  MessageSquare, Sparkles, Loader2, BookOpen, Send, Database, Tag,
  ShieldCheck, Brain, FileText, Trash2, ChevronRight
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODES = [
  { value: "prose",        label: "Prose",        icon: BookOpen,    hint: "Long-form analyst answer with regulatory references" },
  { value: "json",         label: "Structured",   icon: FileText,    hint: "Machine-readable JSON with key_points + regulatory_references" },
  { value: "investigator", label: "Investigator", icon: ShieldCheck, hint: "Audit-friendly summary with risk implications + confidence line" },
];

const SUGGESTED_QUERIES = [
  "What is the penalty under PMLA for a reporting entity that fails to file an STR within 7 working days?",
  "When must Enhanced Due Diligence be performed and what does it require?",
  "What are the compliance obligations for a foreign Politically Exposed Person?",
  "Define 'proceeds of crime' under PMLA Section 2(1)(u) and how it differs from a scheduled offence.",
  "What records must a reporting entity maintain and for how long under PML Rules?",
];

function CitationChip({ c }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      background: "rgba(59,130,246,0.10)", border: "1px solid rgba(59,130,246,0.35)",
      borderRadius: 999, padding: "5px 11px", fontSize: 11, fontWeight: 500,
      color: "#93c5fd", marginRight: 8, marginBottom: 6, fontFamily: "ui-monospace, monospace"
    }}>
      <BookOpen style={{ width: 11, height: 11 }} />
      <span style={{ color: "#e2e8f0" }}>{c.title || c.doc_id || c.source}</span>
      <span style={{ color: "#64748b" }}>·</span>
      <span style={{ color: "#94a3b8" }}>{(c.score * 100).toFixed(0)}%</span>
    </div>
  );
}

function KeyPointBullet({ children }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "6px 0" }}>
      <ChevronRight style={{ width: 14, height: 14, color: "#60a5fa", marginTop: 3, flexShrink: 0 }} />
      <div style={{ color: "#cbd5e1", fontSize: 13, lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

function Pill({ children, color = "#60a5fa" }) {
  return (
    <span style={{
      display: "inline-block",
      background: `${color}1a`, border: `1px solid ${color}55`, color,
      fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 999,
      marginRight: 6, marginBottom: 4, fontFamily: "ui-monospace, monospace"
    }}>{children}</span>
  );
}

export default function ComplianceCopilotPage() {
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("prose");
  const [topK, setTopK] = useState(5);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);     // {query, response, ts}
  const [corpusStats, setCorpusStats] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const responseRef = useRef(null);

  useEffect(() => { void fetchCorpusStats(); }, []);

  async function fetchCorpusStats() {
    try {
      const t = localStorage.getItem("token");
      const { data } = await axios.get(`${API}/rag/corpus/stats`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setCorpusStats(data);
    } catch (e) { /* silent */ }
  }

  async function seedCorpus() {
    setSeeding(true);
    try {
      const t = localStorage.getItem("token");
      const { data } = await axios.post(`${API}/rag/seed`, {}, {
        headers: { Authorization: `Bearer ${t}` },
      });
      toast.success(`Seeded ${data.total_files || 0} regulatory documents`);
      await fetchCorpusStats();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(`Seed failed: ${detail}`);
    } finally {
      setSeeding(false);
    }
  }

  async function ask(q = query) {
    if (!q || q.trim().length < 3) {
      toast.error("Please type a question (min 3 chars)");
      return;
    }
    setLoading(true);
    try {
      const t = localStorage.getItem("token");
      const { data } = await axios.post(`${API}/rag/ask`,
        { query: q.trim(), mode, top_k: topK },
        { headers: { Authorization: `Bearer ${t}` }, timeout: 60_000 },
      );
      const turn = { query: q.trim(), response: data, ts: new Date().toISOString() };
      setHistory(h => [turn, ...h]);
      setQuery("");
      setTimeout(() => responseRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(`Copilot error: ${detail}`);
    } finally {
      setLoading(false);
    }
  }

  function clearHistory() {
    setHistory([]);
    toast.info("Conversation cleared (knowledge base untouched)");
  }

  const totalCorpusChunks = (corpusStats?.corpora || []).reduce((a, c) => a + (c.chunk_count || 0), 0);
  const totalCorpusDocs = (corpusStats?.corpora || []).reduce((a, c) => a + (c.document_count || 0), 0);

  return (
    <div style={{ maxWidth: 1240, margin: "0 auto", padding: "24px" }}>
      {/* Header */}
      <div className="flex items-center" style={{ gap: 12, marginBottom: 6 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(139,92,246,0.12)", border: "1px solid rgba(139,92,246,0.40)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Brain style={{ width: 20, height: 20, color: "#a78bfa" }} />
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={{ color: "#f1f5f9", fontSize: 26, fontWeight: 700, margin: 0, letterSpacing: "-.3px" }}>
            Compliance <span style={{ color: "#a78bfa" }}>Copilot</span>
          </h1>
          <p style={{ color: "#94a3b8", margin: "2px 0 0 0", fontSize: 13 }}>
            Ask any PMLA / RBI Master Direction / FATF question — answers are <strong style={{ color: "#e2e8f0" }}>grounded</strong> in your knowledge base, with <strong style={{ color: "#e2e8f0" }}>regulatory citations</strong> and source-chunk attribution.
          </p>
        </div>
        {history.length > 0 && (
          <button
            data-testid="copilot-clear"
            onClick={clearHistory}
            style={{ background: "transparent", border: "1px solid #1e2530", color: "#94a3b8", borderRadius: 8, padding: "8px 12px", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
          >
            <Trash2 style={{ width: 12, height: 12 }} />Clear
          </button>
        )}
      </div>

      {/* Corpus status bar */}
      <div data-testid="copilot-corpus-status" style={{
        background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10,
        padding: "10px 14px", marginTop: 14, display: "flex", alignItems: "center",
        justifyContent: "space-between", gap: 12,
      }}>
        <div className="flex items-center" style={{ gap: 16, flexWrap: "wrap" }}>
          <div className="flex items-center" style={{ gap: 6 }}>
            <Database style={{ width: 14, height: 14, color: "#60a5fa" }} />
            <span style={{ color: "#94a3b8", fontSize: 12 }}>Knowledge base:</span>
            <strong style={{ color: "#e2e8f0", fontSize: 12 }}>{totalCorpusChunks} chunks · {totalCorpusDocs} documents</strong>
          </div>
          {(corpusStats?.corpora || []).map(c => (
            <Pill key={c.corpus}>{c.corpus} ({c.chunk_count})</Pill>
          ))}
        </div>
        {totalCorpusChunks === 0 && (
          <button
            data-testid="copilot-seed"
            onClick={seedCorpus}
            disabled={seeding}
            style={{ background: "linear-gradient(135deg, #7c3aed, #8b5cf6)", color: "white", border: "none", borderRadius: 6, padding: "6px 12px", fontSize: 11, fontWeight: 600, cursor: seeding ? "wait" : "pointer", display: "flex", alignItems: "center", gap: 6 }}
          >
            {seeding ? <Loader2 className="animate-spin" style={{ width: 12, height: 12 }} /> : <Sparkles style={{ width: 12, height: 12 }} />}
            {seeding ? "Seeding…" : "Seed PMLA + RBI Master Directions"}
          </button>
        )}
      </div>

      {/* Mode toggle */}
      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 12, padding: 18, marginTop: 14 }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
          {MODES.map(m => {
            const Icon = m.icon;
            const active = mode === m.value;
            return (
              <button
                key={m.value}
                data-testid={`copilot-mode-${m.value}`}
                onClick={() => setMode(m.value)}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  background: active ? "linear-gradient(135deg, rgba(139,92,246,0.18), rgba(139,92,246,0.10))" : "#080c12",
                  border: `1px solid ${active ? "#8b5cf6" : "#1e2530"}`,
                  color: active ? "#c4b5fd" : "#94a3b8",
                  borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600,
                  cursor: "pointer", transition: "all .15s ease",
                  boxShadow: active ? "0 0 12px rgba(139,92,246,0.25)" : "none",
                }}
              >
                <Icon style={{ width: 14, height: 14 }} />
                {m.label}
              </button>
            );
          })}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
            <label style={{ color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>Top-K</label>
            <input
              data-testid="copilot-topk"
              type="number" min={1} max={15} value={topK}
              onChange={e => setTopK(Math.max(1, Math.min(15, parseInt(e.target.value || "5", 10))))}
              style={{ width: 60, background: "#080c12", border: "1px solid #1e2530", borderRadius: 6, padding: "6px 10px", color: "#e2e8f0", fontSize: 12, fontFamily: "ui-monospace, monospace", outline: "none" }}
            />
          </div>
        </div>
        <div style={{ color: "#64748b", fontSize: 11, marginBottom: 12 }}>
          {MODES.find(m => m.value === mode)?.hint}
        </div>

        {/* Query input */}
        <div style={{ position: "relative" }}>
          <textarea
            data-testid="copilot-query-input"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) ask(); }}
            placeholder="Ask anything about PMLA, RBI KYC Master Direction, FATF Recommendations, STR/CTR obligations…"
            rows={3}
            style={{ width: "100%", background: "#080c12", border: "1px solid #1e2530", borderRadius: 8, padding: "12px 14px", color: "#f1f5f9", fontSize: 14, outline: "none", resize: "vertical", fontFamily: "inherit", lineHeight: 1.55 }}
            disabled={loading}
          />
          <button
            data-testid="copilot-ask-button"
            onClick={() => ask()}
            disabled={loading || !query.trim()}
            style={{
              position: "absolute", bottom: 10, right: 10,
              background: loading ? "#1e2530" : "linear-gradient(135deg, #7c3aed, #8b5cf6)",
              color: "white", border: "none", borderRadius: 8, padding: "10px 18px",
              fontSize: 13, fontWeight: 600, cursor: loading ? "wait" : "pointer",
              display: "flex", alignItems: "center", gap: 6,
              boxShadow: loading ? "none" : "0 0 14px rgba(139,92,246,0.40)",
              opacity: (!query.trim() && !loading) ? 0.5 : 1,
            }}
          >
            {loading ? <Loader2 className="animate-spin" style={{ width: 13, height: 13 }} /> : <Send style={{ width: 13, height: 13 }} />}
            {loading ? "Thinking…" : "Ask"}
          </button>
        </div>

        {/* Suggested queries */}
        {history.length === 0 && !loading && (
          <div style={{ marginTop: 14 }}>
            <div style={{ color: "#64748b", fontSize: 11, marginBottom: 8, textTransform: "uppercase", letterSpacing: 1 }}>Try one of these</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {SUGGESTED_QUERIES.map((q, i) => (
                <button
                  key={i}
                  data-testid={`copilot-suggested-${i}`}
                  onClick={() => { setQuery(q); ask(q); }}
                  disabled={loading}
                  style={{ textAlign: "left", background: "transparent", border: "1px solid #1e2530", borderRadius: 6, padding: "9px 12px", color: "#cbd5e1", fontSize: 12, cursor: "pointer", transition: "all .15s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#11161f"; e.currentTarget.style.borderColor = "#374151"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "#1e2530"; }}
                >
                  <Sparkles style={{ width: 11, height: 11, display: "inline", marginRight: 8, color: "#8b5cf6" }} />
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Conversation thread */}
      <div ref={responseRef} style={{ marginTop: 24 }}>
        {history.map((turn, i) => (
          <div data-testid={`copilot-turn-${i}`} key={i} style={{ marginBottom: 22 }}>
            {/* User question */}
            <div style={{ display: "flex", gap: 12, marginBottom: 10 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(59,130,246,0.10)", border: "1px solid rgba(59,130,246,0.35)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <MessageSquare style={{ width: 14, height: 14, color: "#60a5fa" }} />
              </div>
              <div style={{ flex: 1, color: "#e2e8f0", fontSize: 14, lineHeight: 1.55, padding: "7px 0" }}>
                {turn.query}
              </div>
            </div>

            {/* Assistant response */}
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(139,92,246,0.12)", border: "1px solid rgba(139,92,246,0.40)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Brain style={{ width: 14, height: 14, color: "#a78bfa" }} />
              </div>
              <div data-testid={`copilot-response-${i}`} style={{ flex: 1, background: "#0d1117", border: "1px solid #1e2530", borderRadius: 10, padding: 16 }}>
                {/* If JSON mode, render structured */}
                {turn.response.mode === "json" ? (
                  <>
                    <div style={{ color: "#e2e8f0", fontSize: 14, lineHeight: 1.6, marginBottom: 14, whiteSpace: "pre-wrap" }}>
                      {turn.response.answer}
                    </div>
                    {(turn.response.key_points || []).length > 0 && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ color: "#94a3b8", fontSize: 11, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Key Points</div>
                        {turn.response.key_points.map((p, k) => <KeyPointBullet key={k}>{p}</KeyPointBullet>)}
                      </div>
                    )}
                    {(turn.response.regulatory_references || []).length > 0 && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ color: "#94a3b8", fontSize: 11, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Regulatory References</div>
                        <div>
                          {turn.response.regulatory_references.map((r, k) => <Pill key={k} color="#22c55e">{r}</Pill>)}
                        </div>
                      </div>
                    )}
                    <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
                      <Pill color="#a78bfa">Confidence: {turn.response.confidence || "—"}</Pill>
                      <span style={{ color: "#64748b", fontSize: 11 }}>·</span>
                      <span style={{ color: "#64748b", fontSize: 11 }}>{turn.response.retrieved_chunks} chunks retrieved</span>
                    </div>
                  </>
                ) : (
                  <div style={{ color: "#e2e8f0", fontSize: 14, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                    {turn.response.answer}
                  </div>
                )}

                {/* Citations — common to all modes */}
                {(turn.response.citations || []).length > 0 && (
                  <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px dashed rgba(148,163,184,0.15)" }}>
                    <div style={{ color: "#94a3b8", fontSize: 11, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                      <Tag style={{ width: 11, height: 11 }} />Source Chunks ({turn.response.citations.length})
                    </div>
                    <div>
                      {turn.response.citations.map((c, k) => <CitationChip key={k} c={c} />)}
                    </div>
                  </div>
                )}

                <div style={{ marginTop: 12, color: "#475569", fontSize: 10, fontFamily: "ui-monospace, monospace" }}>
                  {turn.response.ai_mode} · {turn.response.mode} mode · {new Date(turn.ts).toLocaleTimeString()}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
